import React, { useState, useEffect } from 'react';
import { Lead, MessageTemplate, WhatsAppMessage } from '../types';
import { whatsappService } from '../services/api';

interface WhatsAppModalProps {
  lead: Lead;
  onClose: () => void;
}

const WhatsAppModal: React.FC<WhatsAppModalProps> = ({ lead, onClose }) => {
  const [activeTab, setActiveTab] = useState<'send' | 'history'>('send');
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [customMessage, setCustomMessage] = useState('');
  const [selectedPhone, setSelectedPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const availablePhones = [
    lead.reu_telefone_1,
    lead.reu_telefone_2,
    lead.reu_telefone_3,
    lead.reu_telefone_4,
    lead.reu_telefone_5,
    lead.reu_telefone_6,
    lead.reu_telefone_7,
  ].filter((phone): phone is string => phone !== null && phone !== undefined && phone.trim() !== '');

  useEffect(() => {
    if (availablePhones.length > 0) {
      setSelectedPhone(availablePhones[0]);
    }
    loadTemplates();
    if (activeTab === 'history') {
      loadMessageHistory();
    }
  }, [activeTab]);

  const loadTemplates = async () => {
    try {
      const response = await whatsappService.getTemplatesByStatus(lead.status_lead || '');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

  const loadMessageHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await whatsappService.getMessageHistory(lead.id);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleTemplateSelect = (template: MessageTemplate) => {
    setSelectedTemplate(template);
    setCustomMessage(template.content);
  };

  const handleSendMessage = async () => {
    if (!selectedPhone || !customMessage.trim()) {
      alert('Por favor, selecione um telefone e digite uma mensagem');
      return;
    }

    try {
      setLoading(true);
      await whatsappService.sendMessage({
        leadId: lead.id,
        phone: selectedPhone,
        message: customMessage,
        templateId: selectedTemplate?.id
      });
      
      alert('Mensagem enviada com sucesso!');
      setCustomMessage('');
      setSelectedTemplate(null);
      
      // Recarregar histórico se estiver na aba de histórico
      if (activeTab === 'history') {
        loadMessageHistory();
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      alert('Erro ao enviar mensagem. Verifique a configuração da Evolution API.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'text-blue-600';
      case 'delivered': return 'text-green-600';
      case 'read': return 'text-green-800';
      case 'failed': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendente';
      case 'sent': return 'Enviada';
      case 'delivered': return 'Entregue';
      case 'read': return 'Lida';
      case 'failed': return 'Falhou';
      default: return status;
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">
            WhatsApp - {lead.reu_nome} (Processo: {lead.numero_processo})
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-4">
          <button
            onClick={() => setActiveTab('send')}
            className={`px-4 py-2 rounded-md ${
              activeTab === 'send' 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Enviar Mensagem
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-md ${
              activeTab === 'history' 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Histórico de Conversas
          </button>
        </div>

        {activeTab === 'send' && (
          <div className="space-y-4">
            {/* Seleção de telefone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefone:
              </label>
              <select
                value={selectedPhone}
                onChange={(e) => setSelectedPhone(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                {availablePhones.map((phone, index) => (
                  <option key={index} value={phone}>
                    {phone}
                  </option>
                ))}
              </select>
            </div>

            {/* Templates */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Templates:
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className={`p-2 text-left border rounded-md text-sm ${
                      selectedTemplate?.id === template.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="font-medium">{template.name}</div>
                    <div className="text-gray-600 truncate">{template.content}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Mensagem personalizada */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mensagem:
              </label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={4}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="Digite sua mensagem aqui..."
              />
            </div>

            {/* Botões */}
            <div className="flex justify-end space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendMessage}
                disabled={loading || !selectedPhone || !customMessage.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Enviando...' : 'Enviar Mensagem'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            {loadingHistory ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <div className="mt-2">Carregando histórico...</div>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhuma conversa encontrada para este lead.
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto space-y-3">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`p-3 rounded-lg ${
                      message.message_type === 'sent' 
                        ? 'bg-green-100 ml-8' 
                        : 'bg-gray-100 mr-8'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-sm font-medium">
                        {message.message_type === 'sent' 
                          ? `Enviado${message.user_name ? ` por ${message.user_name}` : ''}` 
                          : 'Recebido'}
                      </span>
                      <div className="text-xs text-gray-500">
                        <div>{formatDate(message.created_at)}</div>
                        {message.message_type === 'sent' && (
                          <span className={`${getStatusColor(message.status)} font-medium`}>
                            {getStatusText(message.status)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap">
                      {message.message_content}
                    </div>
                    {message.template_name && (
                      <div className="text-xs text-blue-600 mt-1">
                        Template: {message.template_name}
                      </div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      Para: {message.phone_number}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppModal;